/**
 * Small pure normalizers for the crm-api responses the invoice/quote generator
 * consumes, extracted from `generate-invoice-form.tsx`. crm-api list endpoints
 * have historically returned the rows under a few different envelope shapes, and
 * a contact/user's display name has the same full_name → name → "first last"
 * fallback everywhere — both were copy-pasted across the loaders (and the
 * submit payload). No money / reseller / hooks here — wall-neutral, under the
 * financial-only-allowlisted `generate-invoice-form/` tree.
 */

/** A person-ish record that may carry any of the name fields crm-api returns. */
export interface NamedPerson {
  full_name?: string | null;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

/**
 * Pull the row array out of a crm-api list response, tolerating the envelope
 * variants we've seen: `{ data: [...] }`, `{ <key>: [...] }` (e.g. `customers`
 * / `users`), or a bare array. Anything non-array (or missing) yields `[]`.
 * Mirrors the old `resp.data || resp.<key> || resp || []` + `Array.isArray`
 * guard exactly — `data` is tried first, then each key in order, then the value
 * itself.
 */
export function firstArrayField(data: any, keys: string[]): any[] {
  let value = data?.data;
  for (const key of keys) {
    value = value || data?.[key];
  }
  value = value || data;
  return Array.isArray(value) ? value : [];
}

/**
 * A person's display name: an explicit `full_name`, else `name`, else the
 * trimmed `"first last"`, else an empty string. Used for the sales-person
 * default and the frozen customer name on the submit payload.
 */
export function personDisplayName(person: NamedPerson): string {
  return (
    person.full_name ||
    person.name ||
    `${person.first_name || ''} ${person.last_name || ''}`.trim()
  );
}
